import { JSXElementConstructor, ReactElement } from 'react';
import { Button, Pagination, Select } from 'react-daisyui';

const { Option } = Select;
interface Props {
  numPages: number;
  selectedPage: number;
  rowsPerPage: number;
  selectedRowsPerPage: number[];
  onPageSelect: (page: number) => void;
  onSelectRowsChange: (page: number) => void;
  firstLabel?: string;
  lastLabel?: string;
  pagesToShow?: number;
  /** When provided, shows a "Showing X-Y of Z" summary on the left. */
  totalRecords?: number;
}
// eslint-disable-next-line import/prefer-default-export
export function OSPagination({
  numPages,
  selectedPage,
  rowsPerPage,
  selectedRowsPerPage,
  onPageSelect,
  onSelectRowsChange,
  firstLabel = 'First',
  lastLabel = 'Last',
  pagesToShow = 10,
  totalRecords,
}: Props) {
  function getPageNumbers(
    currentPage: number,
    totalPages: number
  ): { index: number; label: string }[] {
    const halfNumbersToShow = Math.floor(pagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfNumbersToShow);
    const endPage = Math.min(totalPages, startPage + pagesToShow - 1);

    if (endPage - startPage < pagesToShow - 1) {
      startPage = Math.max(1, endPage - pagesToShow + 1);
    }

    const buttonPages: { index: number; label: string }[] = [];

    if (startPage > 1) {
      buttonPages.push({ index: 0, label: firstLabel });
    }

    for (let i = startPage; i <= endPage; i += 1) {
      buttonPages.push({ index: i - 1, label: i.toString() });
    }

    if (endPage < totalPages) {
      buttonPages.push({ index: totalPages - 1, label: lastLabel });
    }

    return buttonPages;
  }

  const buttonData = getPageNumbers(selectedPage, numPages);

  // "Showing X-Y of Z" summary. Clamp the page so the range never goes out of
  // bounds when rows-per-page changes before the parent resets the page.
  const hasSummary = typeof totalRecords === 'number';
  const total = totalRecords ?? 0;
  const page = Math.min(Math.max(1, selectedPage), Math.max(1, numPages));
  const start = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, total);

  let summary = '';
  if (hasSummary) {
    summary =
      total === 0
        ? 'No results'
        : `Showing ${start}-${end} of ${totalRecords} records`;
  }

  const renderPagination = (className: string) => (
    <Pagination className={className}>
      <>
        <span className='text-sm'>Rows per page:</span>
        <Select
          size='sm'
          bordered={false}
          className='mx-2'
          data-testid='paginationSelect'
          defaultValue={rowsPerPage}
          onChange={(rows: number) => onSelectRowsChange(rows)}
        >
          {selectedRowsPerPage.map((option) => (
            <Option value={option} key={`${option}_option`}>
              {option}
            </Option>
          ))}
        </Select>
      </>
      {
        /* eslint-disable */
        buttonData.map((data) => {
          return (
            <span key={`${data.index + 1 }_span`}>
            {data.label === lastLabel ? (<span>...</span>) :  null}
            <Button
              size='sm'
              onClick={() => onPageSelect(data.index + 1)}
              className={`${data.index + 1 === selectedPage ? 'btn-active' : ''} ${
                data.index === 0 ? ' first-button' : ''
              }`}
              key={`${data.index + 1 }_button`}
              >
              {data.label}
            </Button>
            {data.label === firstLabel ? (<span>...</span>) :  null}
              </span>
          );
        }) as unknown as ReactElement<any, string | JSXElementConstructor<any>>
        /* eslint-enabled */
      }
    </Pagination>
  );

  // Tables that don't pass totalRecords keep the original right-aligned footer.
  if (!hasSummary) {
    return renderPagination('m-4 inline text-right');
  }

  return (
    <div className='bg-base-100 -mt-2'>
      <div className='my-2 mx-4 flex items-center justify-between'>
        <span className='text-sm opacity-70'>{summary}</span>
        {renderPagination('inline')}
      </div>
    </div>
  );
}